import Image from 'next/image';
import styled, { css, type RuleSet } from 'styled-components';
import { type BreakpointKeys, Sizes } from 'styles/constants';
import { Colors } from 'styles/enums';
import { mediaQueries } from 'styles/helpers';
import type { HeroContainerTypes, HeroImageContainerTypes, HeroTextBoxProps } from './types';

export const HeroContainer = styled.div<HeroContainerTypes>`
  max-width: ${Sizes.maxWidth}px;
  width: 100%;
  height: auto;
  min-height: 250px;
  position: relative;
  margin-top: 36px;
  margin-bottom: ${({ $expandedImage }) => ($expandedImage ? '100px' : '0px')};
  margin-left: auto;
  margin-right: auto;
  padding: 0px 18px;

  > div[data-cy='Grid'] {
    ${({ $expandedImage }) => generateMainContainerGap($expandedImage)};
    ${({ $expandedImage }) => ($expandedImage ? generateBackground() : '')}
  }
`;

export const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 75%;
  margin-bottom: 16px;
  ${() => generateBackground()}
`;

export const HeroImage = styled(Image)`
  object-fit: cover;
`;

export const HeroTextBox = styled.div<HeroTextBoxProps>`
  ${() => generateBackground()}
  ${({ $visible }) => {
    if (!$visible) return 'display: flex;';
    return Object.keys($visible).reduce((styles: RuleSet<object>, key) => {
      const breakpointKey = key as BreakpointKeys;
      styles.push(
        css`
          ${mediaQueries(
            breakpointKey,
            css`
              display: ${$visible[breakpointKey] ? 'flex' : 'none'};
            `
          )}
        `
      );

      return styles;
    }, []);
  }}
  ${({ $horizontalAlign }) =>
    $horizontalAlign &&
    css`
      justify-content: ${$horizontalAlign};
    `}
  ${({ $verticalAlign }) =>
    $verticalAlign &&
    css`
      align-items: ${$verticalAlign};
    `}
  flex-direction: column;
  [data-cy='Text'] {
    width: 100%;
  }
`;

export const HeroLinkContainer = styled.div`
  max-width: 180px;
  margin: 35px 0px;

  svg {
    width: 60px;
  }

  ${mediaQueries(
    'md',
    css`
      max-width: 180px;
      margin: 50px 0px;
    `
  )}
`;

const generateDimensions = (rounded: boolean | null | undefined, height: number, expandedImage?: boolean) => {
  return css`
    ${mediaQueries(
      'xs',
      css`
        width: ${rounded ? '194px' : `${height}px`};
        height: ${rounded ? '194px' : `${height}px`};
        max-height: 300px;
        max-width: 280px;
      `
    )}
    ${mediaQueries(
      'md',
      css`
        width: ${height}px;
        height: ${height}px;
        max-height: 400px;
        max-width: ${expandedImage ? '600px' : '400px'};
      `
    )}
  `;
};

const generateBorder = (rounded: boolean | null | undefined, styled: boolean | null | undefined) => {
  const borderThickness = 16;
  const boxShadow = `${borderThickness}px ${borderThickness}px`;
  const boxColor = styled ? Colors.Yellow.base100 : '';
  const borderRadius = rounded ? '50%' : '0px';
  const border = `${borderThickness}px solid ${Colors.Yellow.base100}`;

  return `
    border-radius: ${borderRadius};
    ${
      !rounded && styled
        ? `
      box-shadow: ${boxShadow} ${boxColor};
      margin-left: -${borderThickness}px;
      margin-bottom: ${borderThickness}px;
    `
        : ''
    }
    ${rounded && styled ? `border: ${border};` : ''}
  `;
};

const generateBackground = () => css`
  position: relative;
  z-index: 1;
  background-color: ${Colors.UI.beigeWhite};
`;

const generateMainContainerGap = (expandedImage?: boolean) => css`
  gap: ${expandedImage ? '0px' : '30px'};

  ${mediaQueries(
    'sm',
    css`
      gap: ${expandedImage ? '0px' : '36px'};
    `
  )}
`;

export const HeroImageContainer = styled.div<HeroImageContainerTypes>`
  ${({ $rounded, $height, $expandedImage }) => generateDimensions($rounded, $height, $expandedImage)}
  ${({ $rounded, $styled }) => generateBorder($rounded, $styled)}
  margin-left: ${({ $expandedImage }) => ($expandedImage ? '36px' : '0px')};
`;
